import { type ReactNode } from 'react';
import { C } from '@ridgewill/brand';
import { Screen } from './Screen.js';

/**
 * The layout every RECORD shares: back link, header, tabs, panels.
 *
 * ── Record detail views are SCREENS, not modals (Jon, 2026-08-28) ────
 *
 * Every record here opened a dialog: an account, an application, an agreement,
 * an activity event. Security groups did not, because it has a paged, searchable
 * members table and a paged table inside a 640px dialog is a list you cannot
 * read while doing the thing you opened it for. That exception was the tell —
 * the reasoning applies to all of them, it was simply loudest there.
 *
 * What a dialog costs on this surface, concretely:
 *
 *   - The record is not a location. `#/users/abc` opened one only if that
 *     account happened to be in the current page of results, and the Users
 *     screen carried a message saying so: "That account is not in the current
 *     results. Clear the search to open it." A link pasted into an incident
 *     channel worked or did not depending on the reader's search box.
 *   - Escape and the backdrop close it, which is right for a confirmation and
 *     wrong for something you are reading and editing across five tabs.
 *   - The width is fixed and the page behind it is frozen, so a record with
 *     genuinely tabular content gets a column of it.
 *
 * MODALS SURVIVE FOR CONFIRMATIONS AND PICKERS. Publishing a version, retiring
 * an application, adding members: things you say yes or no to and leave. Those
 * are the shape a dialog is for.
 *
 * ── Why this is one component rather than four similar headers ───────
 *
 * The four records had four hand-rolled headers before this, and they had
 * already drifted — one put the slug in the subtitle, one in a code element
 * beside the title, one showed the retired badge and one did not. A shared
 * layout is what makes "the same record, different screens" true rather than
 * aspirational.
 */
export function RecordScreen({
  back,
  avatar,
  title,
  identifier,
  badges,
  description,
  actions,
  tabs,
  base,
  current,
  children,
}: {
  /** Where the back link goes, and what the place is called. */
  back: { to: string; label: string };
  /**
   * A face for the record, left of its heading (Jon, 2026-08-28).
   *
   * Only accounts have one. An application or an agreement is not a person, and
   * a coloured monogram beside "Terms of Service" would be decoration inventing
   * an identity — so this is a slot rather than something derived from `title`.
   */
  avatar?: ReactNode;
  /**
   * THE NAME, not the slug — the chip rule (Jon, 2026-08-28). A chip is an
   * identifier so it carries the slug; this is the heading of the record, which
   * is what a person calls the thing. The slug goes in `identifier`, in
   * monospace beside it.
   */
  title: ReactNode;
  identifier?: ReactNode;
  badges?: ReactNode;
  description?: ReactNode;
  /** The right of the header. Destructive things live here, next to the record
   *  they affect, rather than in a screen-level action row. */
  actions?: ReactNode;
  /** Omit for a record whose content is one set of stacked panels. */
  tabs?: RecordTabs;
  /** The record's own path, without a tab: `/users/abc123`. */
  base?: string;
  current?: string;
  children: ReactNode;
}) {
  return (
    <Screen>
      {/*
        A real anchor rather than a button, for the same reason the sidebar's
        links are: middle click, copy link, and a status bar that says where it
        goes. A hash link needs no click handler at all — the browser writes the
        hash and `useRoute` hears the `hashchange`.
      */}
      <a href={`#${back.to}`} style={s.back}>
        &larr; {back.label}
      </a>

      <header style={s.head}>
        {avatar}
        <div style={{ display: 'grid', gap: '.35rem', minWidth: 0 }}>
          <div style={s.titleRow}>
            <h2 style={s.title}>{title}</h2>
            {identifier ? <code style={s.identifier}>{identifier}</code> : null}
            {badges}
          </div>
          {description ? <div style={s.description}>{description}</div> : null}
        </div>
        {actions ? <div style={{ marginLeft: 'auto', flex: 'none' }}>{actions}</div> : null}
      </header>

      {tabs && base ? <TabNav base={base} tabs={tabs} current={current} /> : null}

      {children}
    </Screen>
  );
}

export type RecordTab<T extends string = string> = { path: T; label: string };

/**
 * A tab strip is NON-EMPTY by construction, and the type says so.
 *
 * `resolveTab` falls back to the first tab, which means it reads `tabs[0]` —
 * and under `noUncheckedIndexedAccess` that is `RecordTab | undefined`, so a
 * plain array forces either a non-null assertion or a branch for a case that
 * cannot happen. The tuple removes both: a caller passing an empty strip is a
 * type error rather than a runtime one.
 */
export type RecordTabs<T extends string = string> = readonly [RecordTab<T>, ...RecordTab<T>[]];

/**
 * Tabs that are ADDRESSES.
 *
 * ── The tab is a path segment, like System health ────────────────────
 *
 * `#/users/abc123/access` rather than a `useState` inside a dialog. Jon chose
 * this shape from a mock-up, and the reason is the one that made System health
 * split its sections the same way (org-admin#57): a link should be able to point
 * at somebody's access, not at "the users screen, then find them, then click the
 * second tab".
 *
 * The old `ModalTabs` could not do this and was right not to try. A modal is not
 * a location, so a tab that wrote the hash would have put a dialog's internal
 * state into the URL and made Back close it in a way nothing else did. Removing
 * the dialog is what removes that objection.
 *
 * ── Anchors, not buttons, and no click handler ───────────────────────
 *
 * Same reasoning as the sidebar. An onClick-only control has none of the
 * browser's own affordances, and these are addresses now.
 */
export function TabNav({
  base,
  tabs,
  current,
  label = 'Sections',
}: {
  base: string;
  tabs: RecordTabs;
  current?: string;
  label?: string;
}) {
  const active = resolveTab(tabs, current);
  return (
    <nav style={s.tabs} aria-label={label}>
      {tabs.map((t) => {
        const on = t.path === active;
        return (
          <a
            key={t.path}
            href={`#${base}/${t.path}`}
            aria-current={on ? 'page' : undefined}
            style={tabStyle(on)}
          >
            {t.label}
          </a>
        );
      })}
    </nav>
  );
}

/**
 * Which tab a path segment selects, falling back to the FIRST rather than to
 * nothing.
 *
 * Exported and pure because the fallback is the whole behaviour and it has no
 * visible failure mode: an unrecognised segment would simply render no panel,
 * which reads as a load that never finishes rather than as a bad link. A
 * mistyped URL, or a bookmark from before a tab was renamed, has to land
 * somewhere useful.
 */
export function resolveTab<T extends string>(tabs: RecordTabs<T>, segment?: string): T {
  const match = tabs.find((t) => t.path === segment);
  return match ? match.path : tabs[0].path;
}

/**
 * One tab's style, exported so the rule below can be asserted (org-admin#12).
 *
 * THE RULE: **every state writes the same KEYS.** Here that is the three
 * `borderBottom*` longhands in the base and `borderBottomColor` in the active
 * override — never the `borderBottom` shorthand alongside them.
 *
 * org-admin#12 was the mix. The base set the shorthand and the active state
 * overrode the longhand, so on deactivation React cleared the longhand, did not
 * reapply the shorthand, and the property fell back to its initial value: BLACK.
 * The tab you had just left kept a black underline. Measured in a browser before
 * fixing — the computed style was `border-bottom: 2px rgb(0, 0, 0)` on the
 * inactive tab and `rgba(0, 0, 0, 0)` on the active one, exactly inverted.
 *
 * Either convention works on its own. A style object is not the kind of thing
 * this repo usually tests, and this one earns it because the bug is invisible in
 * the source — both versions read as "the active tab has a blue underline" — and
 * only appears on the SECOND render of a tab, which is precisely the case a
 * glance at the diff misses.
 */
export function tabStyle(active: boolean): Record<string, unknown> {
  return { ...s.tab, ...(active ? s.tabActive : {}) };
}

const s = {
  back: {
    justifySelf: 'start' as const,
    fontSize: '.85rem',
    fontWeight: 500,
    color: C.muted,
    textDecoration: 'none',
    padding: '.1rem 0',
  },
  head: {
    display: 'flex',
    // CENTRED, not top-aligned, so the avatar sits against the heading rather
    // than floating above a two-line block when a description is present.
    alignItems: 'center',
    gap: '.85rem',
    flexWrap: 'wrap' as const,
    padding: '0 .1rem .2rem',
  },
  titleRow: { display: 'flex', alignItems: 'center', gap: '.5rem', flexWrap: 'wrap' as const },
  title: { margin: 0, fontSize: '1.25rem', letterSpacing: '-.01em' },
  identifier: { fontSize: '.8rem', color: C.muted },
  description: { margin: 0, fontSize: '.88rem', color: C.muted, maxWidth: '62ch', lineHeight: 1.5 },
  tabs: {
    display: 'flex',
    gap: '.35rem',
    borderBottom: `1px solid ${C.border}`,
    paddingBottom: '.1rem',
  },
  tab: {
    padding: '.45rem .7rem',
    borderRadius: '7px 7px 0 0',
    fontSize: '.88rem',
    fontWeight: 550,
    color: C.muted,
    textDecoration: 'none',
    /**
     * Reserves the active underline's height on every tab, so switching does not
     * shift the row by two pixels.
     *
     * LONGHAND in BOTH states, deliberately, and the rule is that the two must
     * never be mixed. org-admin#12 was a base style setting the `borderBottom`
     * SHORTHAND with an active state overriding `borderBottomColor` alone: React
     * clears the longhand on deactivation, does not reapply the shorthand, and
     * the property falls back to its initial value — black. The tab you just
     * left kept a black underline.
     *
     * Either convention works as long as every update writes the same key. This
     * is the one System health already used for exactly this control.
     */
    borderBottomWidth: 2,
    borderBottomStyle: 'solid' as const,
    borderBottomColor: 'transparent',
    marginBottom: -1,
  },
  /**
   * Heavier AND darker, not blue.
   *
   * Two channels rather than one: dropping the underline as the only cue would
   * leave nothing for anyone who cannot separate brand blue from grey. The blue
   * stays on the RULE, where it marks position — a tab label coloured like a
   * hyperlink reads as a destination, and although these do navigate now, they
   * navigate within the record rather than away from it.
   */
  tabActive: { color: C.text, fontWeight: 640, borderBottomColor: C.brand },
};
